import type { HTMLAttributes, ReactNode, Ref } from "react";
import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { CheckboxGroup as BaseCheckboxGroup } from "@base-ui/react/checkbox-group";
import classNames from "classnames";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import { CheckGlyph, IndeterminateGlyph } from "./CheckboxGlyphs";
import styles from "./Checkbox.module.scss";

// Visual source of truth: docs/specs/2026-08-27-checkbox-figma-rework.md.
// Base UI renders a hidden input sibling after each root, so positional
// selectors must use :not(:first-child), never the adjacent-sibling combinator.

interface CheckboxGroupProps {
    /** Names the group for assistive tech when no visible heading labels it. */
    "aria-label"?: string;
    /** Child checkbox `name`s currently ticked. */
    value: string[];
    onValueChange?: (value: string[]) => void;
    /** All child checkbox names; required for a `parent` checkbox to compute its state. */
    allValues?: string[];
    disabled?: boolean;
    className?: string;
    children?: ReactNode;
    ref?: Ref<HTMLDivElement>;
}

export const CheckboxGroup = ({
    "aria-label": ariaLabel,
    value,
    onValueChange,
    allValues,
    disabled = false,
    className,
    children,
    ref,
}: Readonly<CheckboxGroupProps>) => (
    <BaseCheckboxGroup
        ref={ref}
        aria-label={ariaLabel}
        value={value}
        onValueChange={onValueChange}
        allValues={allValues}
        disabled={disabled}
        className={classNames(styles.group, className)}
    >
        {children}
    </BaseCheckboxGroup>
);

/**
 * DOM props a wrapping trigger (tooltip, popover) clones onto its child: event
 * handlers and attributes land on the focusable root.
 */
type CheckboxRootDomProps = Omit<
    HTMLAttributes<HTMLElement>,
    | "children"
    | "className"
    | "defaultChecked"
    | "onBlur"
    | "onChange"
    | "role"
    | "style"
    | `aria-${string}`
>;

interface CheckboxProps extends CheckboxRootDomProps {
    checked?: boolean;
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    /** Mixed state: shows the line glyph and aria-checked="mixed". */
    indeterminate?: boolean;
    disabled?: boolean;
    label?: ReactNode;
    /** Keeps `label` as the accessible name but hides it visually. */
    labelHidden?: boolean;
    /** "start" renders the label first and moves the control to the row's end; the container controls row width. */
    labelPlacement?: "end" | "start";
    /**
     * Form field name. Inside a CheckboxGroup it also identifies this checkbox in
     * the group's value array (Base UI binds via `value`, falling back to `name`).
     */
    name?: string;
    /** Marks this checkbox as the group's select-all parent (requires allValues on the group). */
    parent?: boolean;
    /** Announced as `aria-required`; native `required` would block form submit. */
    required?: boolean;
    readOnly?: boolean;
    /** Sets `aria-invalid` on the checkbox. */
    invalid?: boolean;
    onBlur?: () => void;
    "aria-label"?: string;
    /** Id of a visible element elsewhere that names the checkbox. */
    "aria-labelledby"?: string;
    className?: string;
    "data-testid"?: string;
    /** Playground-only: statically render an interaction state. */
    "data-forced-state"?: "hover" | "pressed" | "focus";
    ref?: Ref<HTMLElement>;
    /** The hidden native input, for form libraries that drive focus or read the node. */
    inputRef?: Ref<HTMLInputElement>;
}

export const Checkbox = ({
    checked,
    defaultChecked,
    onCheckedChange,
    indeterminate = false,
    disabled = false,
    label,
    labelHidden = false,
    labelPlacement = "end",
    name,
    parent = false,
    required = false,
    readOnly = false,
    invalid = false,
    onBlur,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
    ref,
    inputRef,
    ...rootDomProps
}: Readonly<CheckboxProps>) => (
    <BaseCheckbox.Root
        {...rootDomProps}
        ref={ref}
        inputRef={inputRef}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        onBlur={onBlur}
        indeterminate={indeterminate}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        parent={parent}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        {...ariaLabelledbyProps(ariaLabelledby)}
        className={classNames(
            styles.checkbox,
            styles.interactive,
            labelPlacement === "start" && styles.labelStart,
            className,
        )}
        data-testid={dataTestid}
        data-forced-state={forcedState}
    >
        {/* The whole row is the Base UI root, so a label click toggles and the
                accessible name comes from the same element (Radio precedent). */}
        <span className={styles.control}>
            <BaseCheckbox.Indicator className={styles.glyph}>
                <CheckGlyph className={styles.checkGlyph} />
                <IndeterminateGlyph className={styles.indeterminateGlyph} />
            </BaseCheckbox.Indicator>
        </span>
        {label !== undefined && (
            <span
                className={classNames(
                    styles.label,
                    labelHidden && styles.labelHidden,
                )}
            >
                {label}
            </span>
        )}
    </BaseCheckbox.Root>
);
